/**
 * Where the client came from, carried in the address.
 *
 * A session can be opened from Today or from any week — including one three
 * weeks back that the client went looking through deliberately. Coming out of it
 * onto Today would throw away the thing they had just found, which is the most
 * common way a week view becomes annoying (`client-week-and-plan` design D5).
 *
 * So the week a session was opened from travels with it, as `?week=<number>`.
 *
 * **A query parameter rather than router state**, and that is the whole decision
 * in this file. `<Link state>` is held in the history entry and is gone the
 * moment the client reloads, shares the address, or arrives from a notification
 * — and a client reading a session in a gym on a bad connection reloads. The
 * address is the one place a piece of navigation context survives all three.
 *
 * It is a *number*, never an id: it is read straight back into the week route,
 * which is itself keyed by number, and a number that is not the client's renders
 * as not found like any other. Nothing here grants access to anything; the
 * policies decide what a week query returns, exactly as they did before.
 */

/** The week a screen was opened from, or `null` when it was not. */
export function weekParam(search: URLSearchParams): number | null {
  const raw = search.get('week');
  // The same shape the week route accepts. Anything else is not a week number
  // and is dropped rather than repaired: the fallback is Today, which is always
  // a correct place to come out at.
  if (raw === null || !/^[1-9]\d{0,3}$/.test(raw)) return null;
  return Number(raw);
}

/** `?week=3`, or nothing at all — appended to a link to carry the origin on. */
export function weekQuery(week: number | null): string {
  return week === null ? '' : `?week=${String(week)}`;
}
